import React, { useState } from 'react';
import { useNavigate } from 'react-router';
import { css, useTheme } from '@emotion/react';
import { BirdiTheme } from '@birdi/theme';
import { stroot, organisation as organisationAPI } from '@birdi/js-sdk';
import Select from 'react-select';
import { forms } from '@birdi/theme';
import BirdiLogoSVG from '@birdi/icons/birdi-logo-navy.svg';
import GlobeIcon from '@birdi/icons/globe.svg';
import InfoSVG from '@birdi/icons/onboarding-info.svg';
import { Organisations } from '@birdi/types';
import { useMetaSelector, updateMeta } from '../../../meta/reducer';
import { store } from '../../../state';
import { ProgressBar } from '../progress-bar';
import * as styles from '../styles';
import { goToDashboard } from './organisation-setup';

const dataRegionOptions = Organisations.DATA_REGION_OPTIONS;

interface SelectDataRegionProps {}

const SelectDataRegion: React.FC<SelectDataRegionProps> = () => {
  const theme = useTheme() as BirdiTheme;
  const navigate = useNavigate();
  const meta = useMetaSelector();
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const isOrgAdmin = meta.permissions?.includes('organisation:edit');
  const existingRegion = meta.organisation?.s3Region;
  const effectiveRegion = existingRegion || Organisations.DEFAULT_S3_REGION;
  const currentValue =
    dataRegionOptions.find((o) => o.value === effectiveRegion) || null;

  const handleRegionChange = async (option: any) => {
    if (!option?.value) return;
    setSaving(true);
    try {
      await organisationAPI.update(stroot('sjw3fg'), {
        s3Region: option.value,
      });
      store.dispatch(
        updateMeta({
          organisation: {
            ...meta.organisation,
            s3Region: option.value as Organisations.S3Region,
          },
        }),
      );
      setError('');
    } catch (err) {
      console.error('Failed to save data region:', err);
      setError('Failed to save region. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    navigate('/onboarding');
  };

  const handleNext = () => {
    if (!currentValue) {
      setError('Please select a data region.');
      return;
    }
    // @ts-ignore - stripeSubscriptionId exists at runtime but not in type definition
    if (meta.organisation?.stripeSubscriptionId) {
      const scopeRole = (meta as any).scopeRole;
      if (scopeRole === 'Organisation Admin') {
        navigate('/onboarding/invite-your-team');
      } else {
        goToDashboard(meta, navigate);
      }
    } else {
      navigate('/onboarding/select-plan');
    }
  };

  return (
    <>
      <div css={styles.logoContainer}>
        <BirdiLogoSVG />
      </div>

      <div css={styles.scrollableContent}>
        <div css={styles.headerSection}>
          <div css={styles.iconContainer}>
            <div css={styles.iconWrapper(theme)}>
              <GlobeIcon />
            </div>
            <h1 css={styles.title}>Select your data region</h1>
          </div>

          <ProgressBar currentStep={4} totalSteps={6} />

          <p css={styles.subtitle}>
            Choose where your data will be stored to optimise performance and
            access for your team.
          </p>
        </div>

        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: 1.5rem;
            margin-top: 1rem;
          `}
        >
          <div css={styles.formField}>
            <label css={styles.label} htmlFor="dataRegion">
              Location <span className="required">*</span>
            </label>
            <div css={styles.inputWrapper}>
              {existingRegion || !isOrgAdmin ? (
                <>
                  <div css={styles.inputIcon}>
                    <GlobeIcon />
                  </div>
                  <input
                    id="dataRegion"
                    type="text"
                    css={styles.input}
                    value={currentValue?.label || ''}
                    readOnly
                  />
                </>
              ) : (
                <div
                  css={css`
                    width: 100%;
                  `}
                >
                  <Select
                    inputId="dataRegion"
                    instanceId="dataRegion"
                    options={dataRegionOptions}
                    placeholder="Select a data region"
                    value={currentValue}
                    onChange={handleRegionChange}
                    isDisabled={saving}
                    styles={forms.reactSelect}
                  />
                </div>
              )}
            </div>
            {error && <div css={styles.errorMessage}>{error}</div>}
            {!isOrgAdmin && (
              <p
                css={(theme: BirdiTheme) => css`
                  font-size: 0.8125rem;
                  color: ${theme.mono60};
                  margin: 0.5rem 0 0 0;
                `}
              >
                Only organisation admins can change the data region.
              </p>
            )}
          </div>

          <div
            css={(theme: BirdiTheme) => css`
              background-color: ${theme.lightOcean};
              border-radius: 0.5rem;
              padding: 1rem;
              display: flex;
              gap: 0.75rem;
              align-items: flex-start;
            `}
          >
            <div
              css={(theme: BirdiTheme) => css`
                width: 1.25rem;
                height: 1.25rem;
                min-width: 1.25rem;
                color: ${theme.ocean};
                display: flex;
                align-items: center;
                justify-content: center;
              `}
            >
              <InfoSVG />
            </div>
            <div
              css={(theme: BirdiTheme) => css`
                font-size: 0.875rem;
                color: ${theme.deepOcean};
                margin: 0;
                line-height: 1.5;
              `}
            >
              <strong>How do data regions work?</strong>
              <br />
              Your data will be stored in the selected region to improve
              visualisation speed and access for users in that location.
              Currently, all data processing is still performed in Australia.
            </div>
          </div>

          <div
            css={css`
              display: flex;
              gap: 0.75rem;
              margin-top: 0.5rem;
              flex-shrink: 0;
            `}
          >
            <button
              type="button"
              onClick={handleBack}
              css={styles.secondaryButton(theme)}
            >
              ← Back
            </button>
            <button
              type="button"
              onClick={handleNext}
              disabled={saving}
              css={styles.primaryButton(theme)}
            >
              Continue →
            </button>
          </div>
        </div>
      </div>

      <div css={styles.fixedFooter}>
        <div css={styles.helpLink}>
          Need help? <a href="mailto:support@birdi.io">Contact support</a>
        </div>
      </div>
    </>
  );
};

export default SelectDataRegion;
